/**
 * App — root komponent
 *
 * Faza 3 skelet → Faza 6 → Faza 8:
 *   - QueryClientProvider (TanStack Query)
 *   - BrowserRouter + Routes
 *     - /login           (Public, LoginPage)
 *     - / (ProtectedRoute + AppLayout)
 *       - index           HomePage (dashboard)
 *       - /kassa          KassaPage (POS, Faza 8)
 *       - /products       ProductsPage (CRUD)
 *       - *               NotFoundPage
 *   - Toaster (global notifications)
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';

import { AppLayout } from './app/layouts/AppLayout';
import { ProtectedRoute } from './app/routes/ProtectedRoute';
import { RequireRole } from './app/routes/RequireRole';
import { BlockedPage } from './pages/BlockedPage';
import { DemoLoginPage } from './pages/DemoLoginPage';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { TelegramSetupPage } from './pages/TelegramSetupPage';
import { shouldRetryQuery } from './shared/lib/query-defaults';
import { Toaster } from './shared/ui/toast';

// Route-level code-splitting — sekin (xalqaro) tarmoqda birinchi yuk minimal
// bo'lishi uchun og'ir authenticated sahifalar talab bo'yicha (lazy) yuklanadi.
// Public/edge sahifalar (Login/Blocked/Telegram/NotFound) eager — birinchi
// ekran uchun qo'shimcha round-trip bo'lmasin. Named export → default'ga o'raladi.
const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })));
const KassaPage = lazy(() => import('./pages/KassaPage').then((m) => ({ default: m.KassaPage })));
const ProductsPage = lazy(() =>
  import('./pages/ProductsPage').then((m) => ({ default: m.ProductsPage })),
);
const ExcelImportPage = lazy(() =>
  import('./pages/ExcelImportPage').then((m) => ({ default: m.ExcelImportPage })),
);
const ProductDetailPage = lazy(() =>
  import('./pages/ProductDetailPage').then((m) => ({ default: m.ProductDetailPage })),
);
const CategoriesPage = lazy(() =>
  import('./pages/CategoriesPage').then((m) => ({ default: m.CategoriesPage })),
);
const SuppliersPage = lazy(() =>
  import('./pages/SuppliersPage').then((m) => ({ default: m.SuppliersPage })),
);
const CustomersPage = lazy(() =>
  import('./pages/CustomersPage').then((m) => ({ default: m.CustomersPage })),
);
const CustomerDetailPage = lazy(() =>
  import('./pages/CustomerDetailPage').then((m) => ({ default: m.CustomerDetailPage })),
);
const DebtsPage = lazy(() => import('./pages/DebtsPage').then((m) => ({ default: m.DebtsPage })));
const DebtCategoriesPage = lazy(() =>
  import('./pages/DebtCategoriesPage').then((m) => ({ default: m.DebtCategoriesPage })),
);
const DebtCategoryDetailPage = lazy(() =>
  import('./pages/DebtCategoryDetailPage').then((m) => ({ default: m.DebtCategoryDetailPage })),
);
const OrderRequestsPage = lazy(() =>
  import('./pages/OrderRequestsPage').then((m) => ({ default: m.OrderRequestsPage })),
);
const SpecialOrdersPage = lazy(() =>
  import('./pages/SpecialOrdersPage').then((m) => ({ default: m.SpecialOrdersPage })),
);
const CustomerInquiriesPage = lazy(() =>
  import('./pages/CustomerInquiriesPage').then((m) => ({ default: m.CustomerInquiriesPage })),
);
const PriceQuotesPage = lazy(() =>
  import('./pages/PriceQuotesPage').then((m) => ({ default: m.PriceQuotesPage })),
);
const StatsPage = lazy(() => import('./pages/StatsPage').then((m) => ({ default: m.StatsPage })));
const UsersPage = lazy(() => import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })));
const RolesPage = lazy(() => import('./pages/RolesPage').then((m) => ({ default: m.RolesPage })));
const XodimlarPage = lazy(() =>
  import('./pages/XodimlarPage').then((m) => ({ default: m.XodimlarPage })),
);
const StoresPage = lazy(() =>
  import('./pages/StoresPage').then((m) => ({ default: m.StoresPage })),
);
const MarketplaceOrdersPage = lazy(() =>
  import('./pages/MarketplaceOrdersPage').then((m) => ({ default: m.MarketplaceOrdersPage })),
);
const PickupPointsPage = lazy(() =>
  import('./pages/PickupPointsPage').then((m) => ({ default: m.PickupPointsPage })),
);
const PromoCodesPage = lazy(() =>
  import('./pages/PromoCodesPage').then((m) => ({ default: m.PromoCodesPage })),
);
const TenantSettingsPage = lazy(() =>
  import('./pages/TenantSettingsPage').then((m) => ({ default: m.TenantSettingsPage })),
);
const MarketplaceModerationPage = lazy(() =>
  import('./features/marketplace/MarketplaceModerationPage').then((m) => ({
    default: m.MarketplaceModerationPage,
  })),
);
const MarketplaceBannersPage = lazy(() =>
  import('./pages/MarketplaceBannersPage').then((m) => ({ default: m.MarketplaceBannersPage })),
);
const PickupOperatorPage = lazy(() =>
  import('./pages/PickupOperatorPage').then((m) => ({ default: m.PickupOperatorPage })),
);
const CourierPage = lazy(() =>
  import('./pages/CourierPage').then((m) => ({ default: m.CourierPage })),
);
const CourierTrackingPage = lazy(() =>
  import('./pages/CourierTrackingPage').then((m) => ({ default: m.CourierTrackingPage })),
);

export function App(): React.ReactElement {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // staleTime: ma'lumot shu vaqt "yangi" — fonда qayta yuklamaydi.
            staleTime: 60_000, // 60s
            // gcTime: keshlangan ma'lumot xotirada shu vaqt saqlanadi. Uzaytirildi
            // (5→30 daq) — ish-sessiyasida sahifalar orasida navigatsiya DARHOL
            // (keshlangan ko'rsatiladi + fonда yangilanadi = stale-while-revalidate).
            gcTime: 30 * 60_000, // 30 daq
            retry: shouldRetryQuery,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true, // tarmoq qaytsa yangilash (flaky tarmoq uchun)
          },
          mutations: {
            retry: 0,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/demo" element={<DemoLoginPage />} />
          <Route path="/blocked" element={<BlockedPage />} />
          <Route path="/telegram-setup" element={<TelegramSetupPage />} />

          {/* Protected */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              {/* Hammaga ochiq (XODIM ham) */}
              <Route index element={<HomePage />} />
              <Route path="/kassa" element={<KassaPage />} />
              <Route path="/buyurtmalar" element={<OrderRequestsPage />} />
              <Route path="/yangi-buyurtmalar" element={<SpecialOrdersPage />} />
              <Route path="/mijoz-sorovi" element={<CustomerInquiriesPage />} />
              <Route path="/narx-aytildi" element={<PriceQuotesPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:id" element={<ProductDetailPage />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/customers/:id" element={<CustomerDetailPage />} />

              {/* Faqat EGASI/ADMIN — XODIM /kassa'ga yo'naltiriladi */}
              <Route element={<RequireRole allow={['EGASI', 'ADMIN']} />}>
                <Route path="/products/import" element={<ExcelImportPage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/suppliers" element={<SuppliersPage />} />
                <Route path="/debts" element={<DebtsPage />} />
                <Route path="/qarz-kategoriyalar" element={<DebtCategoriesPage />} />
                <Route path="/qarz-kategoriyalar/:id" element={<DebtCategoryDetailPage />} />
                <Route path="/stats" element={<StatsPage />} />
                <Route path="/rollar" element={<RolesPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/xodimlar" element={<XodimlarPage />} />
                <Route path="/settings" element={<TenantSettingsPage />} />
              </Route>

              {/* EGASI — do'konlar ro'yxati */}
              <Route element={<RequireRole allow={['EGASI']} />}>
                <Route path="/stores" element={<StoresPage />} />
              </Route>

              {/* EGASI/ADMIN — marketplace buyurtmalari */}
              <Route element={<RequireRole allow={['EGASI', 'ADMIN']} />}>
                <Route path="/marketplace-orders" element={<MarketplaceOrdersPage />} />
              </Route>

              {/* EGASI — marketplace boshqaruvi (promokod, punktlar) */}
              <Route element={<RequireRole allow={['EGASI']} />}>
                <Route path="/pickup-points" element={<PickupPointsPage />} />
                <Route path="/promo-codes" element={<PromoCodesPage />} />
              </Route>

              {/* EGASI — marketplace bannerlar */}
              <Route element={<RequireRole allow={['EGASI']} />}>
                <Route path="/marketplace/bannerlar" element={<MarketplaceBannersPage />} />
              </Route>

              {/* EGASI — marketplace moderatsiya (katalog merge) */}
              <Route element={<RequireRole allow={['EGASI']} />}>
                <Route path="/marketplace/moderatsiya" element={<MarketplaceModerationPage />} />
              </Route>

              {/* Faqat PICKUP_OPERATOR — punkt buyurtmalari */}
              <Route element={<RequireRole allow={['PICKUP_OPERATOR']} />}>
                <Route path="/pickup-operator" element={<PickupOperatorPage />} />
              </Route>

              {/* Faqat COURIER — kuryer ilovasi (yetkazib berish) */}
              <Route element={<RequireRole allow={['COURIER']} />}>
                <Route path="/courier" element={<CourierPage />} />
              </Route>

              {/* EGASI — kuryerlarni jonli kuzatuv + hisobot */}
              <Route element={<RequireRole allow={['EGASI']} />}>
                <Route path="/courier-tracking" element={<CourierTrackingPage />} />
              </Route>

              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster />
    </QueryClientProvider>
  );
}
